import React, { useState } from 'react';
import PropTypes from 'prop-types';

import ProductPreview from '../../productPreview/ProductPreview';
import {currencyFormat, countFormat} from '../../../util/locale';
import ProductsListItemLikeButton from './ProductsListItemLikeButton';

import './ProductsListItem.css';
import {Link} from "react-router-dom";
import {brandUrl} from "../../../util/url";

/**
 * Products list item component.
 */
export default function ProductsListItem({ product, imageLoading = 'lazy', imageFetchPriority }) {
    const isSold = product.status === 'sold';
    const [isLiked, setIsLiked] = useState(Boolean(product.is_liked));
    const [likeCount, setLikeCount] = useState(product.like_count || 0);

    return (
        <div className={`ProductsListItem${isSold ? ' ProductsListItem--sold' : ''}`}>
            <div className="ProductsListItem-img">
                <ProductPreview product={product} loading={imageLoading} fetchPriority={imageFetchPriority}/>
                {isSold && (
                    <div className="ProductsListItem-sold-badge">SOLD</div>
                )}
                <ProductsListItemLikeButton
                    product={product}
                    isLiked={isLiked}
                    likeCount={likeCount}
                    onLikeChange={({ isLiked: nextIsLiked, likeCount: nextLikeCount }) => {
                        setIsLiked(nextIsLiked);
                        setLikeCount(nextLikeCount);
                    }}
                />
            </div>
            <div className="ProductsListItem-brand-row">
                {product.brand ? (
                    <Link to={brandUrl(product.brand)} className="ProductsListItem-brand">
                        {product.brand}
                    </Link>
                ) : (
                    <span className="ProductsListItem-brand ProductsListItem-brand--missing">No Brand</span>
                )}
                {product.size && <span className="ProductsListItem-size">{product.size}</span>}
            </div>
            <p className="ProductsListItem-title">{product.title}</p>
            {product.condition === 'NEW' && (
                <div className="ProductsListItem-condition">새상품</div>
            )}
            <div className="ProductsListItem-price-row">
                {product.discount_rate && (
                    <span className="ProductsListItem-discount-rate">{`${product.discount_rate}%`}</span>
                )}
                <span className="ProductsListItem-price">
                    {currencyFormat(product.price, 'KRW')}
                </span>
            </div>
            {product.discount_rate > 0 && product.discount_rate < 100 && product.original_price && (
                <div className="ProductsListItem-original-price">
                    {currencyFormat(product.original_price, 'KRW')}
                </div>
            )}
            {(product.view_count > 0 || likeCount > 0) && (
                <div className="ProductsListItem-stats">
                    {product.view_count > 0 && (
                        <div className="ProductsListItem-stat">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                <circle cx="12" cy="12" r="3"/>
                            </svg>
                            <span>{countFormat(product.view_count)}</span>
                        </div>
                    )}
                    {likeCount > 0 && (
                        <div className="ProductsListItem-stat">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                            </svg>
                            <span>{countFormat(likeCount)}</span>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
ProductsListItem.propTypes = {
    product: PropTypes.shape({
        id: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        status: PropTypes.oneOf(['selling', 'sold']).isRequired,
        brand: PropTypes.string,
        external_url: PropTypes.string,
        price: PropTypes.number.isRequired,
        original_price: PropTypes.number,
        resizedSmallImages: PropTypes.arrayOf(PropTypes.string).isRequired,
        size: PropTypes.string,
        condition: PropTypes.string,
        is_liked: PropTypes.bool,
        like_count: PropTypes.number,
        view_count: PropTypes.number,
        discount_rate: PropTypes.number,
    }).isRequired,
    imageLoading: PropTypes.oneOf(['eager', 'lazy']),
    imageFetchPriority: PropTypes.oneOf(['high', 'low', 'auto']),
};
